/**
 * Connexion TLS vers un serveur IMAP, depuis le navigateur.
 *
 * Le navigateur ne sait pas ouvrir de connexion TCP : on passe donc par le
 * relais (/relay), qui transmet les octets tels quels. Le TLS est négocié ici,
 * avec subtls, de bout en bout avec le serveur mail — le relais ne voit que des
 * données chiffrées. Le certificat du serveur est vérifié contre les autorités
 * racines de Mozilla (public/roots.pem).
 */
import { startTls, TrustedCert, WebSocketReadQueue } from 'subtls';
import { ouvrirTls12, magasinAutorites } from './tunnel-tls12.js';
import { FOURNISSEURS } from './fournisseurs.js';

// Messageries qui n'acceptent que TLS 1.2 : elles passent par tunnel-tls12.js.
const SERVEURS_TLS12 = new Set(Object.values(FOURNISSEURS).filter((f) => f.tls12).map((f) => f.host));
let autoritesForge;

let autoritesRacines;

async function chargerAutorites() {
  autoritesRacines ??= fetch('/roots.pem')
    .then((r) => r.text())
    .then((pem) => TrustedCert.databaseFromPEM(pem));
  return autoritesRacines;
}

function ouvrirWebSocket(hote) {
  const url = new URL('/relay', location.href);
  url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('host', hote);
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';
  return new Promise((ok, ko) => {
    ws.addEventListener('open', () => ok(ws), { once: true });
    ws.addEventListener('error', () => ko(new Error('Le relais est injoignable')), { once: true });
  });
}

/**
 * Ouvre une connexion chiffrée vers `hote` (port 993).
 * Renvoie { lire, ecrire, fermer, certificat } — lire() renvoie le prochain
 * morceau déchiffré, ou undefined quand la connexion est fermée.
 */
export async function ouvrirTunnel(hote) {
  if (SERVEURS_TLS12.has(hote)) {
    autoritesForge ??= fetch('/roots.pem').then((r) => r.text()).then(magasinAutorites);
    const [autorites, ws] = await Promise.all([autoritesForge, ouvrirWebSocket(hote)]);
    return ouvrirTls12(hote, {
      envoyer: (octets) => ws.send(octets),
      surDonnees: (fn) => ws.addEventListener('message', (e) => fn(new Uint8Array(e.data))),
      surFermeture: (fn) => ws.addEventListener('close', fn),
      fermer: () => ws.close()
    }, autorites);
  }
  const [autorites, ws] = await Promise.all([chargerAutorites(), ouvrirWebSocket(hote)]);
  const file = new WebSocketReadQueue(ws);
  const { read, write, userCert } = await startTls(
    hote,
    autorites,
    (n, mode) => file.read(n, mode),
    (octets) => ws.send(octets)
  );
  return {
    lire: read,
    ecrire: write,
    fermer: () => ws.close(),
    certificat: userCert
  };
}
